import * as React from "react";
import { useState } from "react";
import { connect } from "react-redux";
import { GlobalState } from "store/configure-store";
import { PatientType } from "model/patient.model";
import { selectUser } from "store/reducers/user-context.reducer";
import { selectUserMap } from "store/reducers/tenant-context.reducer";
import { UsersMap } from "store/types/tenant-context.types";
import { refreshJournalEntries } from "store/actions/journal.actions";
import {
  JournalEntryContainerType,
  JournalEntryItemType,
  JournalEntryType,
} from "model/journal.model";
import {
  selectJournalEntries,
  selectJournalEntryContainers,
  selectJournalEntryItems,
} from "store/reducers/journal.reducer";
import { selectPatient } from "store/reducers/patient.reducer";
import { User } from "model/user.model";
import LoadingScreen from "main/LoadingScreen";
import TimelineItem from "main/Timeline/TimelineItem";
import useInfiniteScroll from "common/hooks/infinite-scroll.hook";
import Page from "ui/Page";
import { PaginatedSearchJournalRequest } from "model/paginated-journal-request.model";
import { PV_TIMELINE } from "common/matomo/track-page-view.constants";
import { useTrackPageView } from "common/matomo/track-page-view.hook";

interface Props {
  journalEntryContainers: Array<JournalEntryContainerType>;
  journalEntriesByContainer: { [containerId: number]: Array<JournalEntryType> };
  journalEntryItems: Array<JournalEntryItemType>;
  patient: PatientType;
  user: User;
  usersList: UsersMap;
  inView: boolean;
  refreshJournalEntries(
    userId: number,
    patientId: number,
    request: PaginatedSearchJournalRequest,
    usersMap: UsersMap
  ): Promise<number[]>;
}

const TimelineContainer = ({
  journalEntryContainers,
  journalEntriesByContainer,
  journalEntryItems,
  patient,
  user,
  usersList,
  refreshJournalEntries,
}: Props) => {
  const [paginatedRequest, setPaginatedRequest] = useState(
    new PaginatedSearchJournalRequest([])
  );
  const { forwardRef, loading } = useInfiniteScroll(0.9, () => {
    return refreshJournalEntries(
      user.id,
      patient.id,
      paginatedRequest,
      usersList
    ).then((ids) => {
      if (ids.length !== 0) {
        const request = Object.assign({}, paginatedRequest, {
          skipIdList: paginatedRequest.skipIdList.concat(ids),
        });
        setPaginatedRequest(request);
      }
      return ids.length;
    });
  });

  useTrackPageView(PV_TIMELINE);

  if (loading && paginatedRequest.skipIdList.length === 0) {
    return <LoadingScreen />;
  } else {
    return (
      <Page.Scrollable>
        <Page.Body ref={forwardRef}>
          <TimelineItem
            containersByYear={orderContainersByYear(journalEntryContainers)}
            journalEntriesByContainer={journalEntriesByContainer}
            journalEntryItems={journalEntryItems}
            users={usersList}
          />
        </Page.Body>
      </Page.Scrollable>
    );
  }
};

const orderContainersByYear = (
  containers: Array<JournalEntryContainerType>
): { [year: number]: Array<JournalEntryContainerType> } => {
  const containersByYear: {
    [year: number]: Array<JournalEntryContainerType>;
  } = {};
  containers.forEach((container: JournalEntryContainerType) => {
    if (containersByYear[container.actionDate.year]) {
      containersByYear[container.actionDate.year].push(container);
    } else {
      containersByYear[container.actionDate.year] = [container];
    }
  });
  return containersByYear;
};

const mapStateToProps = (state: GlobalState) => {
  return {
    journalEntryContainers: selectJournalEntryContainers(state.journal),
    journalEntriesByContainer: selectJournalEntries(state.journal),
    journalEntryItems: selectJournalEntryItems(state.journal),
    patient: selectPatient(state),
    user: selectUser(state),
    usersList: selectUserMap(state),
  };
};

export default connect(mapStateToProps, { refreshJournalEntries })(
  TimelineContainer
);
